<template>
    <div class="tw:flex tw:flex-col tw:gap-4">
        <!-- Error banner -->
        <div
            v-if="error"
            class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm"
        >
            {{ error }}
        </div>

        <!-- Layout de dos columnas -->
        <div class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-4">
            <!-- ── Card: Mapa de butacas ─────────────────────────────────── -->
            <CardFront custom-classes="tw:px-0 tw:py-0 tw:lg:w-2/3">
                <!-- Cabecera de zona -->
                <div class="tw:px-2 tw:py-2 tw:sm:px-4 tw:sm:py-4 tw:flex tw:items-center tw:justify-between tw:rounded-t-lg tw:border-b tw:border-gray-100">
                    <h3 class="tw:text-base tw:font-bold tw:text-gray-900 tw:m-0 tw:uppercase tw:tracking-wide">
                        {{ zoneName || $t('perfil_invitaciones.escenario') }}
                    </h3>
                    <p class="tw:text-xs tw:text-gray-500 tw:m-0 tw:mt-0.5">
                        {{ mapHintLabel }}
                    </p>
                </div>

                <!-- SeatPicker -->
                <div class="tw:p-2 tw:overflow-auto tw:bg-white" >
                    <slot name="picker" :disabled="limitAlcanzado" :max-selection="maxSeats">
                        <SeatPicker
                            :seats="seats"
                            :loading="loading"
                            :selected-ids="selectedIds"
                            :max-selection="maxSeats"
                            :group-by-row="true"
                            :tipo-recinto="tipoRecinto"
                            @select="$emit('toggle-asiento', $event)"
                        />
                    </slot>
                </div>

                <!-- Leyenda -->
                <div class="tw:bg-gray-50 tw:border-t tw:border-gray-100 tw:px-2 tw:py-2 tw:shrink-0 tw:rounded-b-lg">
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:sm:gap-4">
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-green-400 tw:flex-shrink-0"></span>
                            <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                                {{ $t('perfil_invitaciones.disponible') }}
                            </span>
                        </div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-red-500 tw:flex-shrink-0"></span>
                            <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                                {{ $t('perfil_invitaciones.ocupado') }}
                            </span>
                        </div>
                        <div class="tw:flex tw:items-center tw:gap-2">
                            <span class="tw:w-3 tw:h-3 tw:rounded-full tw:bg-blue-400 tw:ring-2 tw:ring-blue-200 tw:flex-shrink-0"></span>
                            <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-gray-500">
                                {{ $t('perfil_invitaciones.seleccionado') }}
                            </span>
                        </div>
                        <div class="tw:flex tw:items-center tw:gap-2 tw:text-[10px] tw:text-gray-400 tw:font-medium">
                            <i class="material-icons tw:text-sm tw:leading-none">touch_app</i>
                            <span>{{ $t('perfil_invitaciones.click_butacas') }}</span>
                        </div>
                    </div>
                </div>
            </CardFront>
            <!-- ── Card: Butacas seleccionadas ──────────────────────────── -->
            <CardFront custom-classes=" tw:px-0 tw:py-0 tw:lg:w-1/3">
                <!-- Cabecera -->
                <div class="tw:flex tw:items-center tw:gap-3 tw:px-2 tw:py-3 tw:border-b tw:border-gray-100 tw:shrink-0">
                    <i class="material-icons tw:text-teal-600 tw:text-xl tw:leading-none">event_seat</i>
                    <h4 class="tw:text-sm tw:font-extrabold tw:uppercase tw:tracking-tight tw:text-gray-900 tw:m-0">
                        {{ sidebarTitle }}
                    </h4>
                </div>

                <!-- Contenido scrollable -->
                <div class="tw:flex tw:flex-col tw:overflow-y-auto tw:p-2" :style="{ minHeight: '50px', maxHeight: '500px' }">
                    <!-- Límite alcanzado -->
                    <div
                        v-if="limitAlcanzado && !loading"
                        class="tw:bg-orange-50 tw:border tw:border-orange-200 tw:text-orange-700 tw:px-3 tw:py-2 tw:rounded-lg tw:text-xs tw:font-medium tw:mb-4 tw:shrink-0"
                    >
                        {{ limitLabel }}
                    </div>

                    <!-- Lista de tickets -->
                    <ul
                        v-if="selectedSeats.length"
                        class="tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-2"
                    >
                        <li
                            v-for="(seat, index) in selectedSeats"
                            :key="seat.id"
                            class=" tw:hover:bg-gray-100 tw:rounded-lg tw:border tw:border-slate-100 tw:shadow-md tw:px-3 tw:py-2.5 tw:flex tw:justify-between tw:items-center tw:transition-colors tw:shrink-0"
                        >
                            <div>
                                <p class="tw:text-[10px] tw:font-extrabold tw:uppercase tw:tracking-widest tw:text-gray-500 tw:m-0 tw:mb-0.5">
                                    {{ ticketLabel(index) }}
                                </p>
                                <div class="tw:flex tw:gap-2 tw:items-baseline tw:flex-wrap">
                                    <span class="tw:font-bold tw:text-sm tw:text-gray-900">
                                        {{ rowLabel }}: {{ seat.row }}
                                    </span>
                                    <span class="tw:font-bold tw:text-sm tw:text-gray-900">
                                        {{ seatLabel }}: {{ seat.number }}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                :aria-label="$t('perfil_invitaciones.quitar_butaca')"
                                class="tw:w-7 tw:h-7 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-gray-500 tw:hover:bg-red-50 tw:hover:text-red-500 tw:transition-colors tw:border-0 tw:bg-transparent tw:cursor-pointer tw:shrink-0 tw:ml-2"
                                @click="$emit('quitar-asiento', seat.id)"
                            >
                                <i class="material-icons tw:text-base tw:leading-none">close</i>
                            </button>
                        </li>
                    </ul>

                    <!-- Empty state -->
                    <div
                        v-else
                        class="tw:flex-1 tw:flex tw:items-center tw:justify-center tw:py-2"
                    >
                        <p class="tw:text-sm tw:text-gray-400 tw:text-center tw:m-0">
                            {{ emptyLabel }}
                        </p>
                    </div>
                </div>

                <!-- Footer: contador -->
                <div class="tw:px-2 tw:py-2 tw:border-t tw:border-gray-100 tw:shrink-0">
                    <div class="tw:flex tw:justify-between tw:items-center">
                        <span class="tw:text-sm tw:text-gray-500 tw:font-medium">
                            {{ totalLabel }}
                        </span>
                        <span class="tw:text-2xl tw:font-extrabold tw:text-teal-600 tw:leading-none">
                            {{ selectedSeats.length }}<span class="tw:text-base tw:font-medium tw:text-gray-400">/{{ maxSeats }}</span>
                        </span>
                    </div>
                </div>
            </CardFront>

        </div>
    </div>
</template>

<script setup>
    import { shallowRef, watch, computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import SeatPicker from '@/modules/front/ticketing/SeatPicker.vue';

    const props = defineProps({
        seats:          { type: Array,   default: () => [] },
        loading:        { type: Boolean, default: false },
        error:          { type: [String, Boolean], default: null },
        selectedSeats:  { type: Array,   default: () => [] },
        maxSeats:       { type: Number,  default: 0 },
        zoneName:       { type: String,  default: '' },
        sidebarTitle:   { type: String,  default: '' },
        limitLabel:     { type: String,  default: '' },
        emptyLabel:     { type: String,  default: '' },
        totalLabel:     { type: String,  default: '' },
        rowLabel:       { type: String,  default: '' },
        seatLabel:      { type: String,  default: '' },
        mapHintLabel:   { type: String,  default: '' },
        ticketLabel:    { type: Function, default: () => '' },
        /** Type of venue for the stage label (e.g. "CANCHA"). */
        tipoRecinto:    { type: String,  default: null },
    });

    defineEmits(['toggle-asiento', 'quitar-asiento']);

    const _cachedSelectedIds = shallowRef(new Set());

    watch(
        () => props.selectedSeats,
        seats => {
            const newIds = new Set(seats.map(s => s.id));
            const current = _cachedSelectedIds.value;
            // Solo actualizar si el contenido realmente cambió
            if (newIds.size !== current.size || ![...newIds].every(id => current.has(id))) {
                _cachedSelectedIds.value = newIds;
            }
        },
        { deep: true }
    );

    const selectedIds = computed(() => _cachedSelectedIds.value);

    const limitAlcanzado = computed(() =>
        props.maxSeats > 0 && props.selectedSeats.length >= props.maxSeats
    );
</script>
